import { useCallback, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { type InfiniteData, useMutation, useQueryClient } from 'react-query'
import styled from 'styled-components'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import { ShowMore } from 'components/ShowMore/ShowMore'
import { addToast } from 'components/Toast/Toast'
import {
  IconReplayGraphRounded
} from 'assets/svg/sprite'
import AvatarHolder from 'components/UI/AvatarHolder/AvatarHolder'
import { PostInfoBottom, PostInfoTop, PostSidebar } from 'components-ref/molecules'
import {
  PostRendererOptions,
  type PostRendererOptionsHandle,
} from 'components-ref/molecules/post/OptionsDropdown/PostRendererOptions'
import { PostStatsControl } from 'components-ref/molecules/post/PostStats/PostStats'
import { useGlobalVideo } from 'context/globalVideoContext'
import ReplayGraphSVG from 'features/VideoPlayer/ReplayGraph'
import { usePostActions } from 'helpers/helperHooks'
import { useDeletePost, useLikePost, usePinPost } from 'helpers/hooks'
import { purchasePremiumPost } from 'services/endpoints/post'
import { usePreviewStore } from 'store/previewStore'
import { type IFile } from 'types/interfaces/IFile'
import { type IInfinitePage, type IPost } from 'types/interfaces/ITypes'

import { Post } from '../Post/Post'

interface Props {
  postIndex: number
  role: string
  postId: string
  postData: IPost
  dataQuery: readonly any[]
  removeFromHistoryCb?: () => void
  userData?: any
  hidePremiumBadge?: boolean
  hideComments?: boolean
  preloadMedia?: boolean
  hidePinned?: boolean
  isInViewport?: boolean
}

export const PostRenderer = ({
  postIndex,
  role,
  postId,
  postData,
  dataQuery,
  removeFromHistoryCb,
  userData,
  hidePremiumBadge = false,
  hideComments = false,
  preloadMedia = false,
  hidePinned,
  isInViewport = true,
}: Props) => {
  const [statsOpen, setStatsOpen] = useState<boolean>(false)
  const queryClient = useQueryClient()

  const addModal = usePreviewStore((s) => s.addModal)
  const setCommentsActive = usePreviewStore((s) => s.setCommentsActive)
  const handleMinimize = usePreviewStore((s) => s.handleMinimize)

  const { t } = useTranslation()

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState<number>(0)
  const [showPreviewVideo, setShowPreviewVideo] = useState(false)
  const { activeMediaId, setActiveMediaId } = useGlobalVideo()
  const hasPreviewVideo =
    !!postData.price &&
    (postData.videos?.some(video => !!video.preview_video_url) ?? false)
  const [purchasing, setPurchasing] = useState(false)

  const { onDeletePostMutate, onPinPostMutate, onPurchasePostMutate } = usePostActions(
    postData?.id,
    dataQuery
  )

  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [showReplayGraph, setShowReplayGraph] = useState(false)
  const [showNoPlayData, setShowNoPlayData] = useState(false)

  const optionsRef = useRef<PostRendererOptionsHandle | null>(null)

  const handleOpenOptionsFromPress = useCallback(() => {
    console.log('[PostRenderer] long press open requested', {
      hasRef: !!optionsRef.current,
      refValue: optionsRef.current,
    })
    optionsRef.current?.open()
  }, [])

  const isSponsoredPost = postData?.sponsored_post === true
  const isTextOnlyPost =
    (postData?.photo_count ?? 0) === 0 &&
    (postData?.video_count ?? 0) === 0 &&
    (postData?.sound_count ?? 0) === 0
  const allFiles = useMemo<IFile[]>(() => {

    const files = [
      ...(postData.photos ?? []),
      ...(postData.videos ?? []),
      ...(postData.sounds ?? []),
    ]

    if (files.length <= 1) return files
    return [...files].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  }, [postData?.photos, postData?.videos, postData?.sounds])

  const renderCountRef = useRef(0)
  renderCountRef.current++

  const like = {
    status: postData.isLiked ?? false,
    count: postData.likes_count ?? 0,
  }

  const onMutateError = (
    _err: any,
    context: {
      cachedData: InfiniteData<IInfinitePage> | undefined
    }
  ) => {
    if (context) {
      queryClient.setQueryData(dataQuery, context.cachedData)
    }
  }

  const { deletePostMutation } = useDeletePost({
    onMutate: async (deletedPostId: string) => {
      await onDeletePostMutate()

      const deletedId = String(deletedPostId)

      const previousData = queryClient.getQueryData<InfiniteData<IInfinitePage>>(dataQuery)

      if (!previousData) {
        return { previousData }
      }

      queryClient.setQueryData<InfiniteData<IInfinitePage>>(dataQuery, {
        ...previousData,
        pages: previousData.pages.map((page) => ({
          ...page,
          page: {
            ...page.page,
            data: {
              ...page.page.data,
              data: (page.page.data.data ?? []).filter((post) => post.id !== deletedId),
            },
          },
        })),
      })

      return { previousData }
    },

    onError: (err: any, _deletedPostId: string, context?: any) => {
      if (context?.previousData) {
        queryClient.setQueryData(dataQuery, context.previousData)
      }

      onMutateError(err, context)
    },
  })

  const onLikeError = (_err?: any, context?: any) => {
    if (context?.cachedData && context?.dataQuery) {
      queryClient.setQueryData(context.dataQuery, context.cachedData)
    }
  }
  const onLikeMutate_x = () => {
    const cached = queryClient.getQueryData<InfiniteData<IInfinitePage>>(dataQuery)

    if (!cached) {
      return {
        cachedData: undefined,
        dataQuery,
      }
    }

    const isLikesHistoryFeed = dataQuery?.[0] === 'getLikedHistory'
    const isCurrentlyLiked = !!postData.isLiked

    const newData: InfiniteData<IInfinitePage> = {
      ...cached,
      pages: cached.pages.map((singleFeedPage: any) => {
        const currentPosts = singleFeedPage?.page?.data?.data ?? []

        const nextPosts =
          isLikesHistoryFeed && isCurrentlyLiked
            ? currentPosts.filter((post: any) => post.id !== postId)
            : currentPosts.map((post: any) =>
                post.id === postId
                  ? {
                      ...post,
                      isLiked: !post.isLiked,
                      likes_count: !post.isLiked
                        ? (post.likes_count ?? 0) + 1
                        : Math.max((post.likes_count ?? 1) - 1, 0),
                    }
                  : post
              )

        return {
          ...singleFeedPage,
          page: {
            ...singleFeedPage.page,
            data: {
              ...singleFeedPage.page.data,
              data: nextPosts,
            },
          },
        }
      }),
    }

    queryClient.setQueryData(dataQuery, newData)

    return {
      cachedData: cached,
      dataQuery,
    }
  }
  const { likePost } = useLikePost(
    postId,
    like.status,
    onLikeMutate_x,
    onLikeError,
    undefined,
    undefined
  )

  const { pin } = usePinPost({
    onMutate: async () => await onPinPostMutate(),
    onSuccess: () => {},
    onError: (err: any, context: { cachedData: InfiniteData<IInfinitePage> | undefined }) => {
      onMutateError(err, context)
    },
  })


  const hasUnlockedAttachment = useMemo(() => {
  const previewFiles = [
    ...(postData?.photos_preview ?? []),
    ...(postData?.videos_preview ?? []),
  ]

  const purchasedFiles = [
    ...(postData?.photos ?? []),
    ...(postData?.videos ?? []),
    ...(postData?.sounds ?? []),
  ]

  return [...previewFiles, ...purchasedFiles].some((file: any) => file?.locked === false)
}, [
  postData?.photos_preview,
  postData?.videos_preview,
  postData?.photos,
  postData?.videos,
  postData?.sounds,
])

const canEngageWithPost = Boolean(postData.is_purchased || hasUnlockedAttachment)

  const purchasePost = useMutation(purchasePremiumPost, {
    onSuccess: (resp: any) => {
      const purchasedPost = resp?.data?.message?.post
      if (purchasedPost) {
        onPurchasePostMutate(purchasedPost)
      }
      setPurchasing(false)
    },
    onError: (resp: any) => {
      setPurchasing(false)
      addToast('error', resp?.response?.data?.message)
    },
  })

const openComments = (
  postIndex: number,
  activeFile: string,
  postData: IPost,
  dataQuery: readonly any[]
) => {
  const activeVideo =
    postData?.videos?.find(video => String(video.id) === String(activeMediaId)) ??
    postData?.videos?.[0]

  setCommentsActive(true)

  if (activeVideo?.id) {
    setActiveMediaId(String(activeVideo.id))
    addModal(postIndex, 0, 'video', postData, dataQuery)
  } else {
    addModal(postIndex, 0, 'photo', postData, dataQuery)
  }

  handleMinimize(false)
}

  const getCounterOffset = (allData: IFile[]) =>
    allData.length > 1 && allData.some((f) => f.type === 'audio' || f.type === 'sound') ? 5 : 0

  const willShowOwnerPrice = role === 'owner' && !!postData?.price

const mediaTotals = useMemo(() => ({
  imgs: postData?.photo_count ?? postData?.photos?.length ?? postData?.photos_preview?.length ?? 0,
  vids: postData?.video_count ?? postData?.videos?.length ?? postData?.videos_preview?.length ?? 0,
}), [
  postData?.photos_count,
  postData?.videos_count,
  postData?.photos?.length,
  postData?.videos?.length,
  postData?.photos_preview?.length,
  postData?.videos_preview?.length,
])

  type Kind = 'img' | 'vid' | 'other'
  const kindOf = (f: IFile): Kind => {
    const url = f?.url ?? ''
    const type = (f as any)?.type ?? ''
    if (type === 'photo' || url.includes('/image')) return 'img'
    if (type === 'video' || url.includes('/video')) return 'vid'
    return 'other'
  }

  const swiperFiles = useMemo(() => {
    return allFiles.filter((f) => {
      const k = kindOf(f)
      return k === 'img' || k === 'vid'
    })
  }, [allFiles])


const activeSlide = swiperFiles[activeSlideIndex] ?? null

const activeVideo =
  activeSlide && kindOf(activeSlide) === 'vid'
    ? activeSlide
    : null

const activeSegments: number[] =
  activeVideo &&
  Array.isArray(activeVideo.aggregated_segment_data)
    ? activeVideo.aggregated_segment_data
    : []

const hasReplayData = activeSegments.some(value => value > 0)

const isPostOwner =
  role === 'owner' ||
  (
    userData?.id != null &&
    postData.user_id != null &&
    String(userData.id) === String(postData.user_id)
  )

const canShowReplayGraphButton =
  isPostOwner &&
  !!activeVideo &&
  !isPlaying &&
  !postData.stories?.length &&
  !postData.live_scheduled_at &&
  (!postData.price || postData.is_purchased)

  const slideMeta = useMemo(() => {
    let imgN = 0
    let vidN = 0
    return swiperFiles.map((f) => {
      const k = kindOf(f)
      if (k === 'img') imgN++
      if (k === 'vid') vidN++
      return { kind: k, idxInKind: k === 'img' ? imgN : vidN }
    })
  }, [swiperFiles])

const totals = useMemo(() => ({
  imgs: postData?.photo_count ?? 0,
  vids: postData?.video_count ?? 0,
}), [
  postData?.photo_count,
  postData?.video_count,
])

  const [counter, setCounter] = useState<{
    imgs: number
    vids: number
    img: number | null
    vid: number | null
  }>(() => {
    const first = slideMeta[0]
    return {
      imgs: totals.imgs,
      vids: totals.vids,
      img: first?.kind === 'img' ? 1 : null,
      vid: first?.kind === 'vid' ? 1 : null,
    }
  })

  const handleUpdateCounters = useCallback(
    (activeIndex: number) => {
      setActiveSlideIndex(activeIndex)

      // Different media = different graph
      setShowReplayGraph(false)
      setShowNoPlayData(false)

      const meta = slideMeta[activeIndex]
      if (!meta) return

      setCounter(prev => {
        const next = {
          imgs: totals.imgs,
          vids: totals.vids,
          img: meta.kind === 'img' ? meta.idxInKind : null,
          vid: meta.kind === 'vid' ? meta.idxInKind : null,
        }

        return prev.imgs === next.imgs &&
          prev.vids === next.vids &&
          prev.img === next.img &&
          prev.vid === next.vid
          ? prev
          : next
      })
    },
    [slideMeta, totals.imgs, totals.vids]
  )

  const handleDelete = useCallback(() => {
    deletePostMutation.mutate(postId)
  }, [deletePostMutation, postId])

  const handlePin = useCallback(() => {
    pin.mutate(postId)
  }, [pin, postId])

  const handleOpenStats = useCallback(() => {
    setStatsOpen(true)
  }, [])

const handleToggleReplayGraph = useCallback(() => {
  if (!activeVideo) return

  if (!hasReplayData) {
    setShowReplayGraph(false)
    setShowNoPlayData(false)

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setShowNoPlayData(true)
      })
    })

    return
  }

  setShowNoPlayData(false)
  setShowReplayGraph(prev => !prev)
}, [activeVideo, hasReplayData])

  const willShowCounters = mediaTotals.vids > 1 || mediaTotals.imgs > 1
  const bottomLeftSlotOccupied = !isPlaying && (willShowOwnerPrice || willShowCounters)
  const bottomOffsetRem = getCounterOffset(allFiles)

  if (postData?.encode_status === 'processing' && userData.role === 'model') {
    return (
      <PostRendererContainer>
        {!postData.stories?.length && !isPlaying && (
          <PostRendererOptions
            ref={optionsRef}
            role={role}
            postId={postId}
            postData={postData}
            allFiles={allFiles}
            removeFromHistoryCb={removeFromHistoryCb}
            onDelete={handleDelete}
            onPin={handlePin}
            onOpenStats={handleOpenStats}
          />
        )}

        <div className="post__files__processing">
          <div className="post__files__processing--bg"></div>
          <div className="post__files__processing__content">
            <div className="processingFile__loader">
              <span className="processingFileLoader"></span>
            </div>
            <div className="post__files__processing__text">{t('yourMediaIsCurrentlyProsessing')}</div>
          </div>
        </div>

        <div className="post__info">
          <div className="post__info__body">
          <AvatarHolder
            img={postData.user.avatar?.url ?? placeholderAvatar}
            size="50"
            userId={postData.user.id}
            sponsored_post={postData.sponsored_post}
            username={postData.user.username}
            dataQuery={dataQuery}
          />
            <ShowMore
              userId={postData.user.id}
              customClass="post"
              name={postData.user.display_name}
              sponsored_post={postData.sponsored_post}
              username={postData.user.username}
              time={postData.created_at}
              tags={['#bowieblue', '#bluechowchow']}
              text={isTextOnlyPost ? '' : (postData.body ?? '')}
              mentions={postData.tags}
            />
          </div>
        </div>
      </PostRendererContainer>
    )
  }

return (
  <PostRendererContainer $hasStories={postData.stories && postData.stories?.length !== 0}>
    <PostStatsControl postData={postData}>
      {({ openStats, toggleStats, isOpen }) => (
        <>
          {!postData.stories?.length && !isPlaying && (
            <PostRendererOptions
              ref={optionsRef}
              role={role}
              postId={postId}
              postData={postData}
              allFiles={allFiles}
              removeFromHistoryCb={removeFromHistoryCb}
              onDelete={handleDelete}
              onPin={handlePin}
              onOpenStats={openStats}
            />
          )}

          {!postData.live?.schedule_date && !postData.stories?.length && (
            <PostSidebar
              hideTip={role === 'owner' || (!!postData.price && !postData.is_purchased)}
              isOwner={role === 'owner'}
              openStats={openStats}
              like={like}
              modelData={{
                postId: postData.id,
                modelId: postData.user.id,
                avatarSrc:
                  postData.user.avatar?.url ??
                  postData.user.cropped_avatar?.url ??
                  placeholderAvatar,
              }}
              onTipModal={() => {}}
              likePost={() => likePost.mutate()}
              commentsCount={postData.comment_count || 0}
              amount={postData.overall_revenue}
              openComments={() => openComments(postIndex, '', postData, dataQuery)}
              isEnabled={canEngageWithPost}
              isPlaying={isPlaying}
              isSponsoredPost={isSponsoredPost}
              hideComments={hideComments}
            />
          )}

          <PostRendererMedia
            className="post-renderer-media"
            data-bottom-slot-empty={(!bottomLeftSlotOccupied).toString()}
            style={{ ['--post-btm-offset' as any]: `${bottomOffsetRem}rem` }}
          >

            {showNoPlayData && !!activeVideo && !isPlaying && (
              <ReplayGraphNoData
                onAnimationEnd={() => setShowNoPlayData(false)}
              >
                No Play Data
              </ReplayGraphNoData>
            )}
          <PostInfoTop
            isPendingReview={postData.pending_review}
            pinned={dataQuery[0] !== 'getHomeFeed' && postData.is_pinned && !hidePinned}
            live={!!postData.live?.active}
            liveEnded={!!postData.live?.finished}
            replayGraphButton={
              canShowReplayGraphButton ? (
                <ReplayGraphPostButton
                  type="button"
                  onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    handleToggleReplayGraph()
                  }}
                  aria-label="Toggle replay graph"
                  aria-pressed={showReplayGraph}
                >
                  <IconReplayGraphRounded color="#fff" />
                </ReplayGraphPostButton>
              ) : null
            }
          />

            <Post
              role={role}
              postIndex={postIndex}
              postData={postData}
              isMyPost={role === 'owner'}
              allFiles={allFiles}
              dataQuery={dataQuery}
              purchaseProcessing={purchasing}
              purchasePostCb={(payload) => {
                if (purchasing) return
                setPurchasing(true)
                purchasePost.mutate(payload)
              }}
              updateCounter={handleUpdateCounters}
              toggleStats={toggleStats}
              userData={userData}
              setVideoCurrentTime={setCurrentTime}
              setIsPlaying={setIsPlaying}
              isPlaying={isPlaying}
              preloadMedia={preloadMedia}
              onOpenOptions={handleOpenOptionsFromPress}
              isInViewport={true}
              showPreviewVideo={showPreviewVideo}
            />

            {showReplayGraph && activeVideo && !isPlaying && (
              <MostPlayed>
                <GraphBar>
                  <ReplayGraphSVG
                    data={activeSegments}
                    height={60}
                  />
                </GraphBar>
              </MostPlayed>
            )}

            <PostInfoBottom
              postId={postData.id}
              role={role}
              price={postData.price}
              mediaCount={counter}
              hideCounter={false}
              countOnly={!!postData.price}
              offset={getCounterOffset(allFiles)}
              premium={hidePremiumBadge ? false : postData.is_purchased && Boolean(postData.price)}
              dataQuery={dataQuery}
              isPlaying={isPlaying}
              tags={postData.tags}
              priceLimits={userData?.price_limits}
              hasPreviewVideo={hasPreviewVideo}
              showPreviewVideo={showPreviewVideo}
              onTogglePreviewVideo={() => setShowPreviewVideo(prev => !prev)}
            />
          </PostRendererMedia>

          {!postData.stories?.length && (
            <div className="post__info">
              <div className="post__info__body">
                <AvatarHolder
                  img={postData.user.avatar?.url ?? placeholderAvatar}
                  size="50"
                  userId={postData.user.id}
                  sponsored_post={postData.sponsored_post}
                  username={postData.user.username}
                  dataQuery={dataQuery}
                />
                <ShowMore
                  userId={postData.user.id}
                  customClass="post"
                  name={postData.user.display_name}
                  username={postData.user.username}
                  sponsored_post={postData.sponsored_post}
                  time={postData.created_at}
                  tags={['#bowieblue', '#bluechowchow']}
                  text={isTextOnlyPost ? '' : (postData.body ?? '')}
                  mentions={postData.tags}
                />
              </div>
            </div>
          )}
        </>
      )}
    </PostStatsControl>
  </PostRendererContainer>
)
}

const PostRendererContainer = styled.div<{ $hasStories?: boolean }>`
  font-size: 1.6rem;
  margin-bottom: 0;
  position: relative;

  ${({ $hasStories }) =>
    $hasStories &&
    `& .postSidebar {
      top: 2.4rem;
    }`}
`

const PostRendererMedia = styled.div`
  position: relative;
  min-height: 22rem;
  overflow: hidden;
`
const ReplayGraphPostButton = styled.button`
  position: relative;
  z-index: 1;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 4px;
  border: none;
  border-radius: 0.6rem;

  color: white;
  cursor: pointer;
  pointer-events: auto;

  background: linear-gradient(
    157.59deg,
    rgba(24, 25, 32, 0.314) 0%,
    rgba(15, 15, 19, 0.314) 100%
  );

  svg {
    width: 1.8rem;
    height: 1.8rem;
  }
`

const ReplayGraphNoData = styled.div`
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;

  padding: 0.4rem 1rem;
  border-radius: 1rem;

  background: rgba(0, 0, 0, 0.30);
  color: #fff;
  font-size: 1.2rem;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;

  animation: noPlayDataFade 2s ease forwards;

  @keyframes noPlayDataFade {
    0% { opacity: 0; }
    12% { opacity: 1; }
    70% { opacity: 1; }
    100% { opacity: 0; }
  }
`

const MostPlayed = styled.div`
  position: absolute;
  left: 0.7rem;
  width: 100%;
  padding: 0 0.1rem;
  -webkit-tap-highlight-color: transparent;
  bottom: 4rem;
  z-index: 10;
  pointer-events: none;

  .post-renderer-media[data-bottom-slot-empty='true'] & {
    left: 1rem;
    bottom: calc(var(--post-btm-offset, 0rem) + 1rem);
  }
`

const GraphBar = styled.div`
  position: absolute;
  bottom: -4.5rem;
  left: -0.6rem;
  width: 100%;
  height: 70px;

  display: flex;
  align-items: flex-end;

  pointer-events: none;
  z-index: 10;
  background: transparent;
  overflow: visible;

  .post-renderer-media[data-bottom-slot-empty='true'] & {
    bottom: -0.5rem;
  }

  svg {
    position: relative;
    z-index: 1;
    display: block;
  }
`